import type { ComboboxRegistry } from './combobox-context.js'
import type { ComboboxOption } from './ComboboxBase.js'
import { ContextProvider } from '@lit/context'
import * as addIcon from '@nordhealth/icons/lib/assets/interface-add-small.js'
import { html, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { repeat } from 'lit/directives/repeat.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import Icon from '../icon/Icon.js'
import { comboboxRegistryContext } from './combobox-context.js'
import style from './Combobox.css'
import ComboboxBase from './ComboboxBase.js'
import { ComboboxClearEvent, ComboboxCreateEvent } from './ComboboxEvents.js'
import '../kbd/Kbd.js'
import '../tag/Tag.js'
import './ComboboxOption.js'
import './ComboboxGroup.js'

Icon.registerIcon(addIcon)

export type { ComboboxOption } from './ComboboxBase.js'
export { ComboboxClearEvent, ComboboxCreateEvent } from './ComboboxEvents.js'

/**
 * Combobox lets users choose one option (or several) from a list by typing to
 * filter, then picking with the keyboard or pointer. Use it when a [Select](/components/select/)
 * would be unwieldy because there are many options to scan.
 *
 * Combobox is a control only — it has no built-in label, hint or error. Compose
 * it inside a [Field](/components/field/) to give it a label, hint and error
 * message, the same way you would a native input.
 *
 * The committed `value` is an option's `value` (or an array of them with
 * `multiple`); the input is a filter/search field whose text is discarded on
 * commit. For a free-text input with suggestions where the typed text is the
 * value, use [Autocomplete](/components/autocomplete/) instead.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot start - Optional slot used to place an icon or prefix at the start of the control.
 * @slot end - Optional slot used to place an icon or suffix at the end of the control.
 *
 * @fires {NordEvent} change - Dispatched when the committed value changes via user interaction.
 * @fires {NordEvent} input - Dispatched as the user types into the search input.
 * @fires {NordEvent} open - Dispatched when the listbox is opened.
 * @fires {NordEvent} close - Dispatched when the listbox is closed.
 * @fires {ComboboxClearEvent} clear - Dispatched when the value is cleared via the clear button or `clear()`.
 * @fires {ComboboxCreateEvent} create - Dispatched when the user chooses the "Create" affordance in a `creatable` combobox.
 *
 * @localization clearLabel - Accessible label for the clear button.
 * @localization createLabel - Label for the "Create" affordance, given the current query.
 * @localization noResults - Message shown when no options match the query.
 *
 * @cssprop [--n-combobox-list-inline-size] - Controls the inline size, or width, of the dropdown list. Defaults to the width of the combobox control.
 * @cssprop [--n-combobox-block-size] - Controls the block size, or height, of the control. The medium default is `var(--n-space-xl)`; the s and l sizes scale from it.
 *
 * @attr {boolean} data-invalid - Present while the control is invalid (mirrors the `invalid` property). Style hook for the whole control.
 * @attr {boolean} data-focused - Present while the control has focus. Style hook for focus-within treatments.
 */
@customElement('nord-combobox')
export default class Combobox extends ComboboxBase {
  /**
   * The value of the combobox. A single string when `multiple` is `false`,
   * or an array of strings when `multiple` is `true`.
   */
  @property({ attribute: false })
  // @ts-expect-error - intentionally widens the inherited `value: string` to support multiple selection
  value: string | string[] = ''

  /**
   * Allows selecting more than one option. Selected options are shown as
   * removable chips and the listbox stays open after each selection.
   */
  @property({ reflect: true, type: Boolean }) multiple = false

  /**
   * Shows a clear (✕) button while there is a value. Pressing it resets the
   * value to `''` (or `[]` when `multiple`), then fires `clear` and `change`.
   * Defaults to `true`.
   *
   * This prop only toggles the button. A single-select value can always be
   * cleared by deleting the input text, regardless of this prop (Base UI
   * parity).
   */
  @property({ reflect: true, type: Boolean }) clearable = true

  /**
   * Offers a "Create" affordance when the query does not exactly match an
   * existing option. Choosing it (click or Enter) adds the typed value as an
   * option and selects it (in `multiple` mode it is appended as a chip), and
   * fires a cancelable `create` event. Call `event.preventDefault()` to opt out
   * and handle creation yourself (e.g. to map the value to a different `value`).
   */
  @property({ reflect: true, type: Boolean }) creatable = false

  /** Options contributed by declarative `<nord-combobox-option>` children. */
  @state() private slottedOptions: ComboboxOption[] = []

  /** Options the combobox created itself via the auto-add `creatable` flow. */
  @state() private createdOptions: ComboboxOption[] = []

  /** Registered declarative option elements (rebuilt into `slottedOptions`). */
  private optionElements = new Set<Element>()

  /** The registry the option/group children use to self-register (provide/inject). */
  private optionRegistry: ComboboxRegistry = {
    add: (option) => {
      this.optionElements.add(option)
      this.rebuildSlottedOptions()
    },
    remove: (option) => {
      this.optionElements.delete(option)
      this.rebuildSlottedOptions()
    },
    notify: () => this.rebuildSlottedOptions(),
  }

  private registryProvider = new ContextProvider(this, {
    context: comboboxRegistryContext,
    initialValue: this.optionRegistry,
  })

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
  }

  /** Read a registered option element into a plain `ComboboxOption`. */
  private readOptionElement(el: Element): ComboboxOption {
    const group = el.closest('nord-combobox-group')?.getAttribute('label') || undefined
    const label = el.getAttribute('label') ?? el.textContent?.trim() ?? ''
    const option: ComboboxOption = {
      value: el.getAttribute('value') ?? '',
      label,
      disabled: el.hasAttribute('disabled'),
    }
    if (group) {
      option.group = group
    }
    // Custom markup (e.g. an avatar) becomes the rendered row content.
    if (el.children.length > 0) {
      option.html = el.innerHTML
    }
    return option
  }

  /** Rebuild `slottedOptions` from the registered elements, in DOM order. */
  private rebuildSlottedOptions() {
    const ordered = [...this.optionElements].sort((a, b) =>

      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    )
    this.slottedOptions = ordered.map(el => this.readOptionElement(el))
  }

  /** Cache for the merged option list so `filtered`'s identity check stays stable. */
  private resolvedCache?: {
    options: ComboboxOption[]
    slotted: ComboboxOption[]
    created: ComboboxOption[]
    result: ComboboxOption[]
  }

  /**
   * The options to render: the `.options` property when set (dynamic/async data),
   * otherwise the declarative `<nord-combobox-option>` children, with any
   * auto-created options merged on top.
   */
  protected override get resolvedOptions(): ComboboxOption[] {
    const base = this.options.length ? this.options : this.slottedOptions
    const created = this.createdOptions
    const cache = this.resolvedCache
    if (cache && cache.options === this.options && cache.slotted === this.slottedOptions && cache.created === created) {
      return cache.result
    }
    const result = created.length ? [...base, ...created] : base
    this.resolvedCache = { options: this.options, slotted: this.slottedOptions, created, result }
    return result
  }

  /** Whether chip/array logic applies — i.e. `multiple` selection. */
  private get isMultiple(): boolean {
    return this.multiple
  }

  /**
   * In `multiple` mode the input is always a pure filter (selections live in
   * chips). In single-select it shows the live query only once the user types;
   * until then it keeps the selected option's label visible and editable (Base
   * UI parity — see `ComboboxBase.showsQueryInInput`).
   */
  protected override get showsQueryInInput(): boolean {
    return this.isMultiple || this.queryDirty
  }

  protected override get inputPlaceholder(): string | undefined {
    return this.isMultiple && this.hasValue ? undefined : this.placeholder
  }

  /** The index of the create row, or `-1` when it isn't shown. */
  protected override get createIndex(): number {
    return this.canCreate ? this.filteredOptions.length : -1
  }

  /** Whether the "Create" affordance should be offered for the current query. */
  protected override get canCreate(): boolean {
    const query = this.search.trim()

    if (!this.creatable || !query) {
      return false
    }

    // Don't offer to create something that already exists exactly.
    return !this.resolvedOptions.some(
      option => option.label.toLocaleLowerCase() === query.toLocaleLowerCase(),
    )
  }

  protected override get showClear(): boolean {
    return this.clearable && this.hasValue && !this.disabled && !this.readonly
  }

  protected override get ariaMultiselectable(): 'true' | 'false' {
    return this.isMultiple ? 'true' : 'false'
  }

  /**
   * Clear the current value, firing `clear` then `change`.
   */
  override clear() {
    this.value = this.isMultiple ? [] : ''
    this.search = ''
    this.queryDirty = false
    this.dispatchEvent(new ComboboxClearEvent())
    this.commit()
    this.focus()
  }

  protected override selectOption(option: ComboboxOption) {
    // Single-select behaves exactly like the base (set value, close, restore
    // focus); only the multiple case — toggle membership, keep the popup open —
    // differs, so handle that here and delegate the rest.
    if (!this.isMultiple) {
      return super.selectOption(option)
    }

    if (option.disabled) {
      return
    }

    // Remember the label so it survives the option leaving the result set.
    this.labelCache.set(option.value, option.label)

    const next = this.values.includes(option.value)
      ? this.values.filter(value => value !== option.value)
      : [...this.values, option.value]

    this.value = next
    this.search = ''
    this.queryDirty = false
    this.commit()
    // Keep the popup open after selection in multiple mode.
    this.focus()
  }

  protected override onInput(input: HTMLInputElement): void {
    // Base UI parity: fully deleting the single-select input text clears the
    // committed selection (not just the filter). This is always available,
    // independent of the `clearable` button. In multiple mode the selections
    // live in chips, so an empty filter is not a clear (Backspace removes the
    // last chip instead — see `onUnhandledKeydown`).
    if (!this.isMultiple && input.value === '' && this.hasValue) {
      this.value = ''
      this.commit()
    }
  }

  protected override emitCreate() {
    const query = this.search.trim()

    if (!query) {
      return
    }

    /**
     * Fired when the user chooses the create affordance, carrying the typed
     * query. By default the combobox then adds the value as an option and selects
     * it; call `event.preventDefault()` to opt out and handle creation yourself.
     */
    const proceed = this.dispatchEvent(new ComboboxCreateEvent(query))
    if (!proceed) {
      // Consumer opted out — leave options/value untouched (legacy behaviour).
      return
    }

    const option: ComboboxOption = { value: query, label: query }

    // Add to our own additive list so it survives `.options` reassignment and
    // works regardless of the source (property, slotted, or external-filtering).
    if (!this.resolvedOptions.some(o => o.value === option.value)) {
      this.createdOptions = [...this.createdOptions, option]
    }
    this.labelCache.set(option.value, option.label)

    // Select it: single sets the value and closes; multiple appends a chip,
    // clears the query and keeps the popup open — same as picking any option.
    this.selectOption(option)
  }

  protected override onUnhandledKeydown(e: KeyboardEvent) {
    // In multiple mode, an empty query Backspace removes the last chip.
    if (e.key === 'Backspace' && this.isMultiple && !this.search && this.values.length) {
      this.removeValue(this.values[this.values.length - 1])
    }
  }

  private removeValue(value: string) {
    if (this.disabled) {
      return
    }

    this.value = this.values.filter(v => v !== value)
    this.commit()
    this.focus()
  }

  /** Multiple mode renders selected options as removable chips before the input. */
  protected override renderLeadingControl() {
    if (!this.isMultiple) {
      return nothing
    }

    return repeat(
      this.values,
      value => value,
      (value) => {
        return html`
          <nord-tag
            class="n-chip"
            size="s"
            variant="removable"
            ?disabled=${this.disabled}
            @remove=${() => this.removeValue(value)}
            @click=${(e: Event) => e.stopPropagation()}
          >
            ${this.labelFor(value)}
          </nord-tag>
        `
      },
    )
  }

  protected override renderCreate() {
    const active = this.activeDescendant.activeIndex === this.createIndex

    return html`
      <div
        id=${this.createId}
        class=${classMap({ 'n-option': true, 'n-create': true, 'is-active': active })}
        role="option"
        aria-selected="false"
        @click=${this.emitCreate}
        @mousemove=${() => this.activeDescendant.setActiveIndex(this.createIndex)}
      >
        <nord-icon class="n-create-icon" name="interface-add-small"></nord-icon>
        <span class="n-option-label">${this.localize.term('createLabel', this.search.trim())}</span>
        <nord-kbd class="n-create-kbd" aria-hidden="true">⏎</nord-kbd>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-combobox': Combobox
  }
}
